/// <reference types="@types/google.maps" />
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import MapIcon from '@mui/icons-material/Map';
import TvIcon from '@mui/icons-material/Tv';
import {
  Box,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { orange } from '@mui/material/colors';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useGetList } from 'react-admin';
import { Wrapper, Status } from '@googlemaps/react-wrapper';
import Permission from '../../components/Permission';
import { PERMISSIONS } from '../../constants';
import { getChainId } from '../../lib';
import { useLiveMapSocket, LiveMapEvent } from '../../hooks/useLiveMapSocket';
import { useCountdown } from '../../hooks/useCountdown';

// ─── Constants ────────────────────────────────────────────────
const POLL_INTERVAL_WITH_SOCKET = 600;  // polling a cada 10min quando socket conectado
const POLL_INTERVAL_WITHOUT_SOCKET = 15; // polling rápido como fallback
const REFRESH_INTERVAL = POLL_INTERVAL_WITHOUT_SOCKET; // default inicial
const SOCKET_FLUSH_MS = 500;             // coalesce rajadas de eventos do socket num único re-render

const OFFLINE_POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutos
const OFFLINE_COLOR = '#b71c1c';                // vermelho sangue
const OFFLINE_GLOW = 'rgba(183, 28, 28, 0.7)';

const STATUS_CONFIG: Record<string, { label: string; main: string; glow: string }> = {
  delivering:              { label: 'Abastecendo',            main: '#00e676', glow: 'rgba(0, 230, 118, 0.6)' },
  deliveringWithoutFollowUp: { label: 'Abastecendo',          main: '#00e676', glow: 'rgba(0, 230, 118, 0.6)' },
  reserved:                { label: 'Reservado',              main: '#448aff', glow: 'rgba(68, 138, 255, 0.6)' },
  pendingReserve:          { label: 'Reserva Pendente',       main: '#ffab00', glow: 'rgba(255, 171, 0, 0.6)' },
  pendingAuthorization:    { label: 'Autorização Pendente',   main: '#ce93d8', glow: 'rgba(206, 147, 216, 0.6)' },
  authorized:              { label: 'Autorizado',             main: '#26c6da', glow: 'rgba(38, 198, 218, 0.6)' },
  waiting:                 { label: 'Aguardando',             main: '#26c6da', glow: 'rgba(38, 198, 218, 0.6)' },
  error:                   { label: 'Erro',                   main: '#ff1744', glow: 'rgba(255, 23, 68, 0.6)' },
};

const BRAZIL_CENTER = { lat: -15.792487, lng: -47.893526 };
const BRAZIL_ZOOM = 4.8;
// Zoom máximo ao auto-enquadrar — evita zoom absurdo numa rede de 1 posto só.
const MAX_FIT_ZOOM = 13;
// Margem (px) ao redor dos pontos no fitBounds — a "margenzinha de sobra".
const FIT_PADDING = 80;

// ─── Radar CSS injection (once) ──────────────────────────────
let radarStyleInjected = false;
function injectRadarStyles() {
  if (radarStyleInjected) return;
  radarStyleInjected = true;
  const style = document.createElement('style');
  style.textContent = `
    @keyframes liveMapRadar {
      0% { transform: scale(1); opacity: 1; }
      100% { transform: scale(3); opacity: 0; }
    }
    @keyframes liveMapFadeOut {
      0% { transform: scale(1); opacity: 1; filter: brightness(1); }
      30% { transform: scale(1.8); opacity: 0.9; filter: brightness(2.5); }
      100% { transform: scale(0); opacity: 0; filter: brightness(0); }
    }
    .live-map-marker.fade-out {
      animation: liveMapFadeOut 1.2s ease-in forwards !important;
      pointer-events: none;
    }
    .live-map-marker.fade-out::after,
    .live-map-marker.fade-out::before {
      animation: none !important;
    }
    .live-map-marker {
      position: relative;
      width: 14px;
      height: 14px;
      border-radius: 50%;
      border: 2px solid rgba(255,255,255,0.9);
      cursor: pointer;
      transition: transform 0.2s;
    }
    .live-map-marker:hover {
      transform: scale(1.4);
      z-index: 10;
    }
    .live-map-marker::after {
      content: '';
      position: absolute;
      inset: -2px;
      border-radius: 50%;
      border: 2px solid currentColor;
      animation: liveMapRadar 2s ease-out infinite;
    }
    .live-map-marker::before {
      content: '';
      position: absolute;
      inset: -2px;
      border-radius: 50%;
      border: 2px solid currentColor;
      animation: liveMapRadar 2s ease-out 1s infinite;
    }
    .live-map-marker.offline {
      width: 21px;
      height: 21px;
      border-width: 3px;
      z-index: 5;
    }
    .live-map-marker.offline::after,
    .live-map-marker.offline::before {
      border-width: 3px;
    }
    .live-map-tooltip {
      position: absolute;
      left: calc(100% + 10px);
      top: 50%;
      transform: translateY(-50%);
      background: rgba(13, 15, 24, 0.92);
      backdrop-filter: blur(8px);
      border: 1px solid rgba(255,255,255,0.12);
      border-radius: 8px;
      padding: 8px 12px;
      white-space: nowrap;
      pointer-events: none;
      opacity: 0;
      transition: opacity 0.2s;
      z-index: 100;
    }
    .live-map-marker:hover .live-map-tooltip {
      opacity: 1;
    }
    .live-map-tooltip-title {
      color: #e8eaed;
      font-size: 12px;
      font-weight: 600;
      margin-bottom: 2px;
    }
    .live-map-tooltip-detail {
      color: #8b95a5;
      font-size: 11px;
    }
    .live-map-tooltip-status {
      font-size: 11px;
      font-weight: 600;
    }
    .live-map-tooltip-divider {
      height: 1px;
      background: rgba(255,255,255,0.12);
      margin: 6px 0;
    }
  `;
  document.head.appendChild(style);
}

// ─── Dark map style for Google Maps ──────────────────────────
const DARK_MAP_STYLES: google.maps.MapTypeStyle[] = [
  { elementType: 'geometry', stylers: [{ color: '#0a0c14' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#0a0c14' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#4a5568' }] },
  { featureType: 'administrative', elementType: 'geometry.stroke', stylers: [{ color: '#1a1d2e' }] },
  { featureType: 'administrative.country', elementType: 'geometry.stroke', stylers: [{ color: '#2d3148' }, { weight: 1.5 }] },
  { featureType: 'administrative.province', elementType: 'geometry.stroke', stylers: [{ color: '#1e2136' }, { weight: 0.8 }] },
  { featureType: 'administrative.country', elementType: 'labels.text.fill', stylers: [{ color: '#5a6178' }] },
  { featureType: 'administrative.province', elementType: 'labels.text.fill', stylers: [{ color: '#3d4458' }] },
  { featureType: 'landscape', elementType: 'geometry', stylers: [{ color: '#0e1019' }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'road', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#080a10' }] },
  { featureType: 'water', elementType: 'labels', stylers: [{ visibility: 'off' }] },
];

// ─── Types ───────────────────────────────────────────────────
interface FillinOnMap {
  id: string;
  stationId: string;
  stationName: string;
  status: string;
  lat: number;
  lng: number;
  fuelName?: string;
  value?: number;
  volume?: number;
  startedAt?: string;
}

interface OfflineStation {
  id: string;
  stationId: string;
  name: string;
  latitude: number;
  longitude: number;
  errorCount: number;
  lastErrorAt: string;
}

// ─── Marker helpers ──────────────────────────────────────────
type StatusCfg = { label: string; main: string; glow: string };

// Escapa strings do backend (nome do posto/combustível) antes de irem pra innerHTML.
function esc(s: string | undefined): string {
  if (!s) return '';
  return s.replace(/[&<>"']/g, c =>
    c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : c === '"' ? '&quot;' : '&#39;',
  );
}

// Status de abastecimento em andamento — só nesses o valor R$ 0,00 faz sentido.
// Em reservado/autorizado, valor 0 = "ainda não começou a abastecer", fica oculto.
const DELIVERING_STATUSES = new Set(['delivering', 'deliveringWithoutFollowUp']);

// Mostra o valor sempre que estiver abastecendo (inclusive R$ 0,00 no começo) ou
// quando já houver valor positivo em qualquer status.
function shouldShowFillinValue(fillin: FillinOnMap): boolean {
  if (DELIVERING_STATUSES.has(fillin.status)) return true;
  return (fillin.value ?? 0) > 0;
}

// Valor em reais no padrão BR (vírgula). Ex.: 0 → "R$ 0,00", 94.5 → "R$ 94,50".
function formatBRL(value: number): string {
  return `R$ ${value.toFixed(2).replace('.', ',')}`;
}

// HTML do tooltip de UM posto. Vários abastecimentos no mesmo posto ficam
// empilhados numa lista (mesmo style), separados por um divider. Usado tanto na
// criação quanto no refresh do marcador — status/combustível/valor mudam ao vivo.
function renderFillinGroupTooltipHTML(stationName: string, items: FillinOnMap[]): string {
  const rows = items.map(fillin => {
    const cfg = STATUS_CONFIG[fillin.status] || STATUS_CONFIG.reserved;
    return `
        <div class="live-map-tooltip-status" style="color:${cfg.main}">${cfg.label}</div>
        ${fillin.fuelName ? `<div class="live-map-tooltip-detail">${esc(fillin.fuelName)}</div>` : ''}
        ${shouldShowFillinValue(fillin) ? `<div class="live-map-tooltip-detail">${formatBRL(fillin.value ?? 0)}</div>` : ''}
      `;
  }).join('<div class="live-map-tooltip-divider"></div>');
  return `<div class="live-map-tooltip-title">${esc(stationName)}</div>${rows}`;
}

// Rótulo acessível (leitor de tela) — marcador não tem texto próprio.
function fillinAriaLabel(fillin: FillinOnMap, cfg: StatusCfg): string {
  const parts = [fillin.stationName, cfg.label];
  if (fillin.fuelName) parts.push(fillin.fuelName);
  if (shouldShowFillinValue(fillin)) parts.push(formatBRL(fillin.value ?? 0));
  return parts.join(' — ');
}

// Rótulo acessível do grupo (um posto, N abastecimentos).
function fillinGroupAriaLabel(stationName: string, items: FillinOnMap[]): string {
  if (items.length === 1) {
    const f = items[0];
    return fillinAriaLabel(f, STATUS_CONFIG[f.status] || STATUS_CONFIG.reserved);
  }
  return `${stationName} — ${items.length} abastecimentos`;
}

// Pulso de brilho quando o status muda — não mexe em transform pra não brigar
// com o scale do :hover. WAAPI reinicia limpo a cada chamada.
function flashStatusChange(el: HTMLElement): void {
  el.animate(
    [{ filter: 'brightness(2.4)' }, { filter: 'brightness(1)' }],
    { duration: 700, easing: 'ease-out' },
  );
}

// ─── Map component ──────────────────────────────────────────
const LiveMapCanvas = React.memo(({
  fillins,
  offlineStations,
}: {
  fillins: FillinOnMap[];
  offlineStations: OfflineStation[];
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  // Map of stationId → { marker, el, repStatus, sig } for incremental updates.
  // Um marcador por POSTO (vários abastecimentos no mesmo posto = um ponto, tooltip em lista).
  // repStatus = status do abastecimento mais recente (cor do ponto); sig = assinatura do grupo (decide refresh).
  const markerMapRef = useRef<Map<string, { marker: google.maps.marker.AdvancedMarkerElement; el: HTMLDivElement; repStatus: string; sig: string }>>(new Map());
  // Map of stationId → { marker, element } for offline stations
  const offlineMarkerMapRef = useRef<Map<string, { marker: google.maps.marker.AdvancedMarkerElement; el: HTMLDivElement }>>(new Map());
  const isFirstRender = useRef(true);
  // Auto-enquadra (fitBounds) só na 1ª vez que há pontos — não fica "puxando" o mapa a cada update.
  const hasFitRef = useRef(false);

  // Init map once
  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;
    injectRadarStyles();

    mapInstanceRef.current = new google.maps.Map(mapRef.current, {
      center: BRAZIL_CENTER,
      zoom: BRAZIL_ZOOM,
      mapId: import.meta.env.VITE_MAP_ID || 'd06eb9fab6b219cd',
      styles: DARK_MAP_STYLES,
      disableDefaultUI: true,
      zoomControl: true,
      backgroundColor: '#0a0c14',
    });
  }, []);

  // Incremental marker update: add new, fade-out removed, update existing.
  // Agrupa por posto — vários abastecimentos no mesmo posto viram UM ponto (mesma
  // lat/lng) com tooltip em lista, em vez de marcadores empilhados/sobrepostos.
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    const groups = new Map<string, FillinOnMap[]>();
    fillins.forEach(f => {
      const arr = groups.get(f.stationId);
      if (arr) arr.push(f);
      else groups.set(f.stationId, [f]);
    });
    // Mais recente primeiro: define a cor do ponto e a ordem da lista no tooltip.
    groups.forEach(items => items.sort((a, b) =>
      new Date(b.startedAt || 0).getTime() - new Date(a.startedAt || 0).getTime()));

    const currentIds = new Set(groups.keys());
    const prevMap = markerMapRef.current;

    // 1. Fade-out postos que sumiram (todos os abastecimentos encerraram)
    if (!isFirstRender.current) {
      prevMap.forEach((entry, stationId) => {
        if (!currentIds.has(stationId)) {
          entry.el.classList.add('fade-out');
          // Remove marker after animation completes
          setTimeout(() => {
            entry.marker.map = null;
            prevMap.delete(stationId);
          }, 1200);
        }
      });
    }
    isFirstRender.current = false;

    // 2. Cria novo marcador / atualiza existente — um por posto
    groups.forEach((items, stationId) => {
      const rep = items[0];
      const cfg = STATUS_CONFIG[rep.status] || STATUS_CONFIG.reserved;
      const sig = items.map(f => `${f.id}:${f.status}:${f.value}:${f.volume}:${f.fuelName || ''}`).join('|');
      const existing = prevMap.get(stationId);

      if (existing) {
        if (existing.el.classList.contains('fade-out')) return;
        if (existing.sig !== sig) {
          const repChanged = existing.repStatus !== rep.status;
          // Cor do ponto = status do abastecimento mais recente
          existing.el.style.backgroundColor = cfg.main;
          existing.el.style.boxShadow = `0 0 6px 2px ${cfg.glow}`;
          existing.el.style.color = cfg.main;
          // Tooltip — status/combustível/valor mudam ao vivo, precisa refresh
          const tip = existing.el.querySelector('.live-map-tooltip') as HTMLDivElement | null;
          if (tip) tip.innerHTML = renderFillinGroupTooltipHTML(rep.stationName, items);
          existing.el.setAttribute('aria-label', fillinGroupAriaLabel(rep.stationName, items));
          // Pulso só quando o status do ponto muda de fato (não a cada poll)
          if (repChanged) flashStatusChange(existing.el);
          existing.repStatus = rep.status;
          existing.sig = sig;
        }
        return;
      }

      // Create new marker
      const el = document.createElement('div');
      el.className = 'live-map-marker';
      el.style.backgroundColor = cfg.main;
      el.style.boxShadow = `0 0 6px 2px ${cfg.glow}`;
      el.style.color = cfg.main;
      el.setAttribute('role', 'img');
      el.setAttribute('aria-label', fillinGroupAriaLabel(rep.stationName, items));

      // Tooltip
      const tooltip = document.createElement('div');
      tooltip.className = 'live-map-tooltip';
      tooltip.innerHTML = renderFillinGroupTooltipHTML(rep.stationName, items);
      el.appendChild(tooltip);

      const marker = new google.maps.marker.AdvancedMarkerElement({
        position: { lat: rep.lat, lng: rep.lng },
        content: el,
        map,
      });

      prevMap.set(stationId, { marker, el, repStatus: rep.status, sig });
    });
  }, [fillins]);

  // Offline stations markers — separate layer, maior e vermelho
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    const currentIds = new Set(offlineStations.map(s => s.stationId));
    const prevMap = offlineMarkerMapRef.current;

    // Remover os que saíram
    prevMap.forEach((entry, id) => {
      if (!currentIds.has(id)) {
        entry.marker.map = null;
        prevMap.delete(id);
      }
    });

    offlineStations.forEach(station => {
      const existing = prevMap.get(station.stationId);
      if (existing) {
        // Atualiza tooltip (contagem pode ter mudado)
        const tip = existing.el.querySelector('.live-map-tooltip') as HTMLDivElement | null;
        if (tip) {
          const lastErr = station.lastErrorAt
            ? new Date(station.lastErrorAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
            : '';
          tip.innerHTML = `
            <div class="live-map-tooltip-title">${esc(station.name)}</div>
            <div class="live-map-tooltip-status" style="color:${OFFLINE_COLOR}">Sem internet</div>
            <div class="live-map-tooltip-detail">${station.errorCount} erros na última hora</div>
            ${lastErr ? `<div class="live-map-tooltip-detail">Último: ${lastErr}</div>` : ''}
          `;
        }
        return;
      }

      const el = document.createElement('div');
      el.className = 'live-map-marker offline';
      el.style.backgroundColor = OFFLINE_COLOR;
      el.style.boxShadow = `0 0 10px 3px ${OFFLINE_GLOW}`;
      el.style.color = OFFLINE_COLOR;
      el.setAttribute('role', 'img');
      el.setAttribute('aria-label', `${station.name} — Sem internet — ${station.errorCount} erros na última hora`);

      const tooltip = document.createElement('div');
      tooltip.className = 'live-map-tooltip';
      const lastErr = station.lastErrorAt
        ? new Date(station.lastErrorAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
        : '';
      tooltip.innerHTML = `
        <div class="live-map-tooltip-title">${esc(station.name)}</div>
        <div class="live-map-tooltip-status" style="color:${OFFLINE_COLOR}">Sem internet</div>
        <div class="live-map-tooltip-detail">${station.errorCount} erros na última hora</div>
        ${lastErr ? `<div class="live-map-tooltip-detail">Último: ${lastErr}</div>` : ''}
      `;
      el.appendChild(tooltip);

      const marker = new google.maps.marker.AdvancedMarkerElement({
        position: { lat: station.latitude, lng: station.longitude },
        content: el,
        map,
        zIndex: 1000,
      });

      prevMap.set(station.stationId, { marker, el });
    });
  }, [offlineStations]);

  // Auto-enquadra: assim que existirem pontos, dá o máximo de zoom mostrando todos
  // (abastecimentos + postos offline) com uma margem de sobra. Roda só uma vez —
  // depois o admin navega/zooma livre sem o mapa "pular" a cada update.
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || hasFitRef.current) return;

    const pts: google.maps.LatLngLiteral[] = [];
    fillins.forEach(f => pts.push({ lat: f.lat, lng: f.lng }));
    offlineStations.forEach(s => pts.push({ lat: s.latitude, lng: s.longitude }));
    if (pts.length === 0) return; // nada pra enquadrar ainda — mantém visão Brasil

    hasFitRef.current = true;

    const bounds = new google.maps.LatLngBounds();
    pts.forEach(p => bounds.extend(p));
    map.fitBounds(bounds, FIT_PADDING);
    // Clamp: 1 posto só (bounds de ponto único) zooma ao máximo — limita pra um nível de cidade.
    google.maps.event.addListenerOnce(map, 'idle', () => {
      if ((map.getZoom() ?? 0) > MAX_FIT_ZOOM) map.setZoom(MAX_FIT_ZOOM);
    });
  }, [fillins, offlineStations]);

  return (
    <div ref={mapRef} style={{ width: '100%', height: '100%' }} />
  );
});

// ─── Stats overlay ───────────────────────────────────────────
const StatsOverlay = ({ fillins, offlineStations }: { fillins: FillinOnMap[]; offlineStations: OfflineStation[] }) => {
  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    fillins.forEach(f => {
      const key = f.status === 'deliveringWithoutFollowUp' ? 'delivering' : f.status;
      counts[key] = (counts[key] || 0) + 1;
    });
    return counts;
  }, [fillins]);

  // Group by station, sorted by count
  const stationCounts = useMemo(() => {
    const map: Record<string, { name: string; count: number }> = {};
    fillins.forEach(f => {
      if (!map[f.stationId]) map[f.stationId] = { name: f.stationName, count: 0 };
      map[f.stationId].count++;
    });
    return Object.values(map).sort((a, b) => b.count - a.count).slice(0, 8);
  }, [fillins]);

  const cardSx = {
    bgcolor: 'rgba(13, 15, 24, 0.85)',
    backdropFilter: 'blur(12px)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 2,
    p: 1.5,
  };

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 16,
        right: 16,
        width: 220,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        zIndex: 10,
        pointerEvents: 'auto',
      }}
    >
      {/* Total */}
      <Box sx={cardSx}>
        <Typography sx={{ color: '#8b95a5', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.1em', mb: 0.5 }}>
          Ao Vivo
        </Typography>
        <Typography sx={{ color: '#e8eaed', fontSize: '1.8rem', fontWeight: 700, lineHeight: 1 }}>
          {fillins.length}
        </Typography>
        <Typography sx={{ color: '#8b95a5', fontSize: '0.7rem' }}>
          abastecimentos ativos
        </Typography>
      </Box>

      {/* Postos offline */}
      {offlineStations.length > 0 && (
        <Box sx={{ ...cardSx, border: `1px solid ${OFFLINE_COLOR}`, boxShadow: `0 0 12px ${OFFLINE_GLOW}` }}>
          <Typography sx={{ color: OFFLINE_COLOR, fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.1em', mb: 1, fontWeight: 700 }}>
            Postos Sem Internet ({offlineStations.length})
          </Typography>
          {offlineStations.map(s => (
            <Box key={s.stationId} sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.4 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: OFFLINE_COLOR, boxShadow: `0 0 6px ${OFFLINE_GLOW}`, flexShrink: 0 }} />
              <Typography sx={{ color: '#e8eaed', fontSize: '0.72rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {s.name}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {/* Status breakdown */}
      <Box sx={cardSx}>
        <Typography sx={{ color: '#8b95a5', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.1em', mb: 1 }}>
          Por Status
        </Typography>
        {Object.entries(STATUS_CONFIG)
          .filter(([key]) => key !== 'deliveringWithoutFollowUp' && statusCounts[key])
          .map(([key, cfg]) => (
            <Box key={key} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: cfg.main, boxShadow: `0 0 4px ${cfg.glow}` }} />
                <Typography sx={{ color: '#c4c9d4', fontSize: '0.72rem' }}>{cfg.label}</Typography>
              </Box>
              <Typography sx={{ color: cfg.main, fontWeight: 700, fontSize: '0.8rem', fontVariantNumeric: 'tabular-nums' }}>
                {statusCounts[key] || 0}
              </Typography>
            </Box>
          ))
        }
      </Box>

      {/* Top stations */}
      {stationCounts.length > 0 && (
        <Box sx={cardSx}>
          <Typography sx={{ color: '#8b95a5', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.1em', mb: 0.8 }}>
            Postos Ativos
          </Typography>
          {stationCounts.map(s => (
            <Box key={s.name} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
              <Typography sx={{ color: '#c4c9d4', fontSize: '0.7rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 150 }}>
                {s.name}
              </Typography>
              <Typography sx={{ color: '#448aff', fontWeight: 600, fontSize: '0.72rem', fontVariantNumeric: 'tabular-nums' }}>
                {s.count}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

// ─── Header ─────────────────────────────────────────────────
const LiveMapHeader = ({
  onExit,
  onEnter,
  isTvMode,
  countdown,
  total,
  socketConnected = false,
}: {
  onExit: () => void;
  onEnter: () => void;
  isTvMode: boolean;
  countdown: number;
  total: number;
  socketConnected?: boolean;
}) => {
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 2.5,
        py: 1,
        background: 'linear-gradient(90deg, #0d0f18 0%, #1a1d2e 100%)',
        borderBottom: '1px solid rgba(255,255,255,0.07)',
        flexShrink: 0,
        zIndex: 20,
      }}
    >
      {/* Left */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <MapIcon sx={{ color: '#448aff', fontSize: '1.3rem' }} />
        <Box>
          <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '0.9rem', lineHeight: 1.2 }}>
            Mapa de Abastecimentos
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.68rem', textTransform: 'capitalize' }}>
            {now.toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}
          </Typography>
        </Box>
      </Box>

      {/* Legend */}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
        {[
          { status: 'delivering', label: 'Abastecendo' },
          { status: 'reserved', label: 'Reservado' },
          { status: 'pendingReserve', label: 'Pendente' },
          { status: 'error', label: 'Erro' },
        ].map(({ status, label }) => {
          const cfg = STATUS_CONFIG[status];
          return (
            <Box key={status} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: cfg.main, boxShadow: `0 0 4px ${cfg.glow}` }} />
              <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.65rem' }}>{label}</Typography>
            </Box>
          );
        })}
      </Box>

      {/* Countdown */}
      <Box sx={{ textAlign: 'center' }}>
        <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.62rem', letterSpacing: '0.08em', textTransform: 'uppercase', mb: 0.3 }}>
          atualização
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
          <Box sx={{ width: 60, height: 3, borderRadius: 99, bgcolor: 'rgba(255,255,255,0.1)', overflow: 'hidden' }}>
            <Box
              sx={{
                height: '100%',
                width: `${(countdown / REFRESH_INTERVAL) * 100}%`,
                bgcolor: countdown <= 3 ? orange[400] : '#448aff',
                borderRadius: 99,
                transition: 'width 1s linear',
              }}
            />
          </Box>
          <Typography
            sx={{
              color: countdown <= 3 ? orange[400] : 'rgba(255,255,255,0.4)',
              fontWeight: 600,
              fontSize: '0.85rem',
              fontVariantNumeric: 'tabular-nums',
              minWidth: 24,
            }}
          >
            {countdown}s
          </Typography>
        </Box>
      </Box>

      {/* Right */}
      <Box display="flex" alignItems="center" gap={1.5}>
        {/* Socket status */}
        <Tooltip title={socketConnected ? 'Atualizações em tempo real' : 'Atualiza a cada 15 segundos'}>
          <Box sx={{
            bgcolor: socketConnected ? 'rgba(0,230,118,0.08)' : 'rgba(255,23,68,0.08)',
            px: 1, py: 0.3, borderRadius: 99,
            display: 'flex', alignItems: 'center', gap: 0.4,
          }}>
            <Box sx={{
              width: 5, height: 5, borderRadius: '50%',
              bgcolor: socketConnected ? '#00e676' : '#ff1744',
            }} />
            <Typography sx={{ color: socketConnected ? '#00e676' : '#ff1744', fontSize: '0.6rem', fontWeight: 600 }}>
              {socketConnected ? 'LIVE' : 'POLL'}
            </Typography>
          </Box>
        </Tooltip>
        <Box sx={{ bgcolor: 'rgba(0,230,118,0.12)', px: 1.5, py: 0.4, borderRadius: 99, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#00e676', animation: 'liveMapRadar 2s ease-out infinite' }} />
          <Typography sx={{ color: '#00e676', fontWeight: 700, fontSize: '0.82rem', fontVariantNumeric: 'tabular-nums' }}>
            {total}
          </Typography>
        </Box>
        <Typography
          sx={{
            color: 'white',
            fontWeight: 300,
            fontSize: '1.4rem',
            fontVariantNumeric: 'tabular-nums',
            letterSpacing: '0.04em',
            lineHeight: 1,
          }}
        >
          {now.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
        </Typography>
        {isTvMode ? (
          <Tooltip title="Sair do Modo TV (ESC)">
            <IconButton
              onClick={onExit}
              sx={{ color: 'rgba(255,255,255,0.4)', '&:hover': { color: 'white', bgcolor: 'rgba(255,255,255,0.08)' } }}
            >
              <FullscreenExitIcon />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title="Modo TV (Fullscreen)">
            <IconButton
              onClick={onEnter}
              sx={{ color: 'rgba(255,255,255,0.4)', '&:hover': { color: 'white', bgcolor: 'rgba(255,255,255,0.08)' } }}
            >
              <TvIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

// ─── Loading ────────────────────────────────────────────────
const LoadingScreen = ({ text }: { text?: string }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', bgcolor: '#0a0c14' }}>
    <Typography sx={{ color: '#8b95a5', fontSize: '0.9rem' }}>{text || 'Carregando mapa...'}</Typography>
  </Box>
);

const renderMapStatus = (status: Status) => {
  if (status === Status.LOADING) return <LoadingScreen text="Carregando Google Maps..." />;
  if (status === Status.FAILURE) return <LoadingScreen text="Erro ao carregar Google Maps" />;
  return <LoadingScreen />;
};

// ─── Main component ─────────────────────────────────────────
const ReportLiveMap = () => {
  const [isTvMode, setIsTvMode] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Fetch stations (once, for lat/lng)
  const { data: stations } = useGetList('stations', {
    pagination: { page: 1, perPage: 9999 },
    sort: { field: 'name', order: 'ASC' },
  });

  // Build station lookup map
  const stationMap = useMemo(() => {
    if (!stations) return new Map<string, { name: string; lat: number; lng: number }>();
    const map = new Map<string, { name: string; lat: number; lng: number }>();
    stations.forEach(s => {
      if (s.latitude && s.longitude) {
        map.set(s.id, { name: s.name || 'Posto', lat: s.latitude, lng: s.longitude });
      }
    });
    return map;
  }, [stations]);

  // Fetch current fillins — filter é estável, refresh usa refetch()
  const chainId = getChainId();
  const fillinFilter = useMemo(() => {
    const now = new Date();
    return {
      chainId,
      from: new Date(now.getTime() - 2 * 60 * 60 * 1000).toISOString(),
      to: new Date(now.setHours(23, 59, 59, 999)).toISOString(),
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chainId]);

  const { data: rawFillins, refetch } = useGetList(`reports/current-fillins`, {
    pagination: { page: 1, perPage: 9999 },
    sort: { field: 'startedAt', order: 'DESC' },
    filter: fillinFilter,
  });

  // --- Postos offline (polling independente a cada 5 minutos) ---
  const { data: offlineRaw, refetch: refetchOffline } = useGetList('reports/offline-stations', {
    pagination: { page: 1, perPage: 9999 },
    sort: { field: 'lastErrorAt', order: 'DESC' },
  });

  useEffect(() => {
    const t = setInterval(() => refetchOffline(), OFFLINE_POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [refetchOffline]);

  const offlineStations = useMemo<OfflineStation[]>(() => {
    if (!offlineRaw || !Array.isArray(offlineRaw)) return [];
    return (offlineRaw as any[])
      .filter(s => s && s.latitude != null && s.longitude != null)
      .map(s => ({
        id: s.id,
        stationId: s.stationId || s.id,
        name: s.name || 'Posto',
        latitude: Number(s.latitude),
        longitude: Number(s.longitude),
        errorCount: Number(s.errorCount) || 0,
        lastErrorAt: s.lastErrorAt,
      }));
  }, [offlineRaw]);

  // --- Socket real-time updates (coalescidos) ---
  const [socketUpdates, setSocketUpdates] = useState<Map<string, FillinOnMap>>(new Map());
  const [socketRemovals, setSocketRemovals] = useState<Set<string>>(new Set());

  // Buffers: rajadas de eventos (sobretudo no "delivering", que pinga valor/volume
  // a cada tick) são agrupadas num único flush a cada SOCKET_FLUSH_MS — evita um
  // re-render por evento.
  const pendingUpdatesRef = useRef<Map<string, FillinOnMap>>(new Map());
  const pendingRemovalsRef = useRef<Set<string>>(new Set());
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flushSocketDeltas = useCallback(() => {
    flushTimerRef.current = null;
    if (pendingUpdatesRef.current.size === 0 && pendingRemovalsRef.current.size === 0) return;
    const ups = pendingUpdatesRef.current;
    const rems = pendingRemovalsRef.current;
    pendingUpdatesRef.current = new Map();
    pendingRemovalsRef.current = new Set();
    setSocketUpdates(prev => {
      const next = new Map(prev);
      ups.forEach((f, id) => next.set(id, f));
      rems.forEach(id => next.delete(id));
      return next;
    });
    setSocketRemovals(prev => {
      const next = new Set(prev);
      rems.forEach(id => next.add(id));
      Array.from(ups.keys()).forEach(id => next.delete(id));
      return next;
    });
  }, []);

  const scheduleFlush = useCallback(() => {
    if (flushTimerRef.current) return;
    flushTimerRef.current = setTimeout(flushSocketDeltas, SOCKET_FLUSH_MS);
  }, [flushSocketDeltas]);

  const handleSocketUpdate = useCallback((data: LiveMapEvent) => {
    const station = stationMap.get(data.stationId);
    if (!station) return;

    const fillin: FillinOnMap = {
      id: data.reserveId,
      stationId: data.stationId,
      stationName: station.name,
      status: data.status || 'reserved',
      lat: station.lat,
      lng: station.lng,
      fuelName: data.fuelName,
      value: data.value || 0,
      volume: data.volume || 0,
      startedAt: data.startedAt,
    };

    pendingUpdatesRef.current.set(data.reserveId, fillin);
    pendingRemovalsRef.current.delete(data.reserveId);
    scheduleFlush();
  }, [stationMap, scheduleFlush]);

  const handleSocketRemove = useCallback((data: LiveMapEvent) => {
    pendingRemovalsRef.current.add(data.reserveId);
    pendingUpdatesRef.current.delete(data.reserveId);
    scheduleFlush();
  }, [scheduleFlush]);

  // Limpa timer de flush pendente ao desmontar
  useEffect(() => () => {
    if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
  }, []);

  const { connected: socketConnected } = useLiveMapSocket(handleSocketUpdate, handleSocketRemove);

  // Polling adaptativo: lento com socket, rápido sem
  const activeInterval = socketConnected ? POLL_INTERVAL_WITH_SOCKET : POLL_INTERVAL_WITHOUT_SOCKET;

  // Refetch on trigger — limpa deltas do socket (poll traz estado completo)
  useEffect(() => {
    if (refreshTrigger > 0) {
      pendingUpdatesRef.current.clear();
      pendingRemovalsRef.current.clear();
      if (flushTimerRef.current) {
        clearTimeout(flushTimerRef.current);
        flushTimerRef.current = null;
      }
      setSocketUpdates(new Map());
      setSocketRemovals(new Set());
      refetch();
    }
  }, [refreshTrigger, refetch]);

  // Join fillins: polling data + socket deltas
  const fillinsOnMap = useMemo<FillinOnMap[]>(() => {
    // 1. Base: dados do polling
    const baseMap = new Map<string, FillinOnMap>();

    if (rawFillins && rawFillins[0]) {
      const fillinData = (rawFillins[0] as any).data || rawFillins;
      if (Array.isArray(fillinData)) {
        fillinData.forEach((f: any) => {
          const station = stationMap.get(f.stationId);
          if (!station) return;
          baseMap.set(f.id || f.reserveId, {
            id: f.id || f.reserveId,
            stationId: f.stationId,
            stationName: station.name,
            status: f.status || 'reserved',
            lat: station.lat,
            lng: station.lng,
            fuelName: f.fuelName || f.stationFuel?.fuelName,
            value: f.value || f.fillInInfo?.value || 0,
            volume: f.volume || f.fillInInfo?.volume || 0,
            startedAt: f.startedAt,
          });
        });
      }
    }

    // 2. Aplica updates do socket (add/update)
    socketUpdates.forEach((fillin, id) => baseMap.set(id, fillin));

    // 3. Remove finalizados/erros do socket
    socketRemovals.forEach(id => baseMap.delete(id));

    return Array.from(baseMap.values());
  }, [rawFillins, stationMap, socketUpdates, socketRemovals]);

  // Countdown & auto-refresh (usa intervalo adaptativo)
  const handleCountdownExpire = useCallback(() => setRefreshTrigger(n => n + 1), []);
  const countdown = useCountdown(activeInterval, handleCountdownExpire);

  // Fullscreen sync
  useEffect(() => {
    const onFsChange = () => { if (!document.fullscreenElement) setIsTvMode(false); };
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  const handleEnterTvMode = useCallback(() => {
    document.documentElement.requestFullscreen().catch(() => {});
    setIsTvMode(true);
  }, []);

  const handleExitTvMode = useCallback(() => {
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
    setIsTvMode(false);
  }, []);

  const mapContent = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: '#0a0c14',
      }}
    >
      <LiveMapHeader
        onExit={handleExitTvMode}
        onEnter={handleEnterTvMode}
        isTvMode={isTvMode}
        countdown={countdown}
        total={fillinsOnMap.length}
        socketConnected={socketConnected}
      />
      <Box sx={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
        <Wrapper
          apiKey={import.meta.env.VITE_MAP_API_KEY || 'AIzaSyA2-gtjUXGbaFwbFy8pmjTPgoV4pTLbx2g'}
          libraries={['marker']}
          render={renderMapStatus}
        >
          <LiveMapCanvas fillins={fillinsOnMap} offlineStations={offlineStations} />
        </Wrapper>
        <StatsOverlay fillins={fillinsOnMap} offlineStations={offlineStations} />
      </Box>
    </Box>
  );

  // TV Mode: fullscreen portal
  if (isTvMode) {
    return createPortal(
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          zIndex: 9999,
          bgcolor: '#0a0c14',
        }}
      >
        {mapContent}
      </Box>,
      document.body,
    );
  }

  // Normal mode
  return (
    <Permission permission={PERMISSIONS.ADMIN}>
      <Box sx={{ height: 'calc(100vh - 48px)', display: 'flex', flexDirection: 'column' }}>
        {mapContent}
      </Box>
    </Permission>
  );
};

export default React.memo(ReportLiveMap);
